import { createTheme } from '@mui/material';

// Freight Warriors palette. `navy` is the app header; `primary` is everything
// interactive in the page body.
export const BRAND = {
  navy: '#0B3C91',
  navyDark: '#082D6E',
  navyLight: '#1B56B8',
  blue: '#1565D8',
  blueDark: '#0F4FAF',
  surface: '#FFFFFF',
  canvas: '#F4F7FC',
  border: '#E2E8F0',
  text: '#0F172A',
  textMuted: '#64748B',
  positive: '#16A34A',
  negative: '#DC2626',
  pending: {
    background: '#FEF3C7',
    text: '#92400E'
  },
  payToDriver: {
    background: '#DBEAFE',
    text: '#1E40AF'
  },
  paid: {
    background: '#DCFCE7',
    text: '#166534'
  }
} as const;

// Avatar colours for driver initials. Picked by hashing the dogtag so a driver
// keeps the same colour everywhere in the app.
const AVATAR_COLORS = ['#1565D8', '#7C3AED', '#0891B2', '#DB2777', '#EA580C', '#059669'];

export const avatarColor = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 100000;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

export const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const theme = createTheme({
  palette: {
    primary: {
      main: BRAND.blue,
      dark: BRAND.blueDark
    },
    secondary: {
      main: '#dc004e'
    },
    background: {
      default: BRAND.canvas,
      paper: BRAND.surface
    },
    text: {
      primary: BRAND.text,
      secondary: BRAND.textMuted
    },
    divider: BRAND.border
  },
  shape: {
    borderRadius: 10
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif'
    ].join(','),
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 }
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: BRAND.textMuted,
          backgroundColor: '#FBFCFE',
          borderBottom: `1px solid ${BRAND.border}`,
          whiteSpace: 'nowrap'
        },
        body: {
          borderBottom: `1px solid ${BRAND.border}`
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 }
      }
    },
    MuiTooltip: {
      defaultProps: { arrow: true }
    }
  }
});

export default theme;
